import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowUp,
  BookmarkSimple,
  CaretDown,
  CircleNotch,
  Copy,
  Check,
  GitFork,
  Lightning,
  MagnifyingGlass,
  Microphone,
  Plus,
  PushPin,
  SidebarSimple,
  Stop,
  Trash,
  X,
} from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useToast } from "../../components/ui/Toast";
import { cn } from "../../lib/cn";
import { LeverMark } from "../../lib/brand";
import { useAIStore } from "../../stores/ai";
import {
  useGlobalChatStore,
  type GCMessage,
  type GCConversation,
  type GroupedConversations,
  type MemoryItem,
} from "../../stores/globalChat";

// ─── Animation configs ───────────────────────────────────────────────────────

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };
const springFast = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 5) return "Working late?";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Burning the midnight oil?";
}

function formatRelative(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "Now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(diff / 86_400_000);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const SUGGESTIONS = [
  { label: "Plan my day", message: "What should I focus on today based on what you know about me?" },
  { label: "Draft something", message: "Help me draft an email or document" },
  { label: "Brainstorm", message: "Let's brainstorm ideas for my current project" },
  { label: "Recall context", message: "What do you remember about my recent work?" },
];

// ─── Markdown renderer ───────────────────────────────────────────────────────

function preprocessMath(text: string): string {
  return text
    .replace(/\\\[(.+?)\\\]/gs, (_m, inner) => `$$${inner}$$`)
    .replace(/\\\((.+?)\\\)/gs, (_m, inner) => `$${inner}$`);
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="prose-chat text-[13px] text-[var(--text-primary)] leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, { strict: false }]]}
        components={{
          pre({ children }) {
            return <CodeBlock>{children}</CodeBlock>;
          },
        }}
      >
        {preprocessMath(content)}
      </ReactMarkdown>
    </div>
  );
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const ref = useRef<HTMLPreElement>(null);

  const handleCopy = useCallback(() => {
    const text = ref.current?.textContent || "";
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);

  return (
    <div className="group/code relative">
      <pre ref={ref}>{children}</pre>
      <button
        onClick={handleCopy}
        className={cn(
          "absolute top-1.5 right-1.5 flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5",
          "text-[9px] font-medium",
          "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
          "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
          "opacity-0 group-hover/code:opacity-100",
          "transition-all duration-100",
          "shadow-[var(--shadow-sm)]",
        )}
      >
        {copied ? (
          <>
            <Check size={9} weight="bold" className="text-[var(--color-signal-green)]" />
            Copied
          </>
        ) : (
          <>
            <Copy size={9} />
            Copy
          </>
        )}
      </button>
    </div>
  );
}

// ─── Conversation sidebar ────────────────────────────────────────────────────

function ConversationSidebar({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();
  const {
    currentConversationId,
    filteredGrouped,
    searchQuery,
    setSearchQuery,
    startNewConversation,
    loadConversation,
    deleteConversation,
  } = useGlobalChatStore();

  const groups = filteredGrouped();
  const searchRef = useRef<HTMLInputElement>(null);

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.aside
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 260, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={spring}
          className="shrink-0 flex flex-col h-full overflow-hidden border-r border-[var(--border-hairline)] bg-[var(--bg-surface)]"
        >
          {/* Sidebar header */}
          <div className="flex items-center gap-2 px-3 pt-3 pb-1.5 shrink-0">
            <motion.button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 min-w-0"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex h-6 w-6 shrink-0 items-center justify-center">
                <LeverMark size={18} className="text-[var(--text-primary)]" />
              </div>
            </motion.button>
            <span
              className="text-[13px] font-semibold text-[var(--text-primary)] flex-1"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Chat
            </span>
            <motion.button
              onClick={startNewConversation}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.9 }}
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)]",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-100",
              )}
              title="New chat"
            >
              <Plus size={13} weight="bold" />
            </motion.button>
            <motion.button
              onClick={onToggle}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.9 }}
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)]",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-100",
              )}
              title="Close sidebar"
            >
              <SidebarSimple size={13} />
            </motion.button>
          </div>

          {/* Search */}
          <div className="px-2 pb-1 shrink-0">
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-sm)]",
                "border border-[var(--border-hairline)] bg-[var(--bg-base)]",
                "px-2 py-1 text-[11px] text-[var(--text-muted)]",
                "focus-within:border-[var(--border-subtle)]",
                "transition-colors duration-100",
              )}
            >
              <MagnifyingGlass size={11} className="shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="flex-1 bg-transparent text-[11px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none min-w-0"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <X size={10} weight="bold" />
                </button>
              )}
            </div>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto px-1.5 py-1">
            {groups.length === 0 ? (
              <div className="px-2 py-8 text-center">
                <p className="text-[11px] text-[var(--text-muted)]">
                  {searchQuery ? "No matches" : "No conversations yet"}
                </p>
              </div>
            ) : (
              groups.map((group) => (
                <ConversationGroup
                  key={group.key}
                  group={group}
                  activeId={currentConversationId}
                  onSelect={loadConversation}
                  onDelete={deleteConversation}
                />
              ))
            )}
          </div>

          {/* Back to dashboard */}
          <div className="shrink-0 border-t border-[var(--border-hairline)] px-2.5 py-2">
            <button
              onClick={() => navigate("/")}
              className={cn(
                "flex items-center gap-2 w-full rounded-[var(--radius-sm)] px-2 py-1.5",
                "text-[11px] font-medium text-[var(--text-muted)]",
                "hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-75",
              )}
            >
              <ArrowLeft size={12} />
              Back to dashboard
            </button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

function ConversationGroup({
  group,
  activeId,
  onSelect,
  onDelete,
}: {
  group: GroupedConversations;
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="mb-1">
      <div className="px-2 py-1">
        <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
          {group.label}
        </span>
      </div>
      <div className="flex flex-col gap-px">
        {group.items.map((conv) => (
          <ConversationItem
            key={conv.id}
            conversation={conv}
            active={conv.id === activeId}
            onSelect={() => onSelect(conv.id)}
            onDelete={() => onDelete(conv.id)}
          />
        ))}
      </div>
    </div>
  );
}

function ConversationItem({
  conversation,
  active,
  onSelect,
  onDelete,
}: {
  conversation: GCConversation;
  active: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const isFork = !!conversation.forked_from_id;

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      layout
      transition={springFast}
      className={cn(
        "group relative flex items-center gap-2 w-full rounded-[var(--radius-sm)] px-2 py-1.5 text-left cursor-pointer",
        "transition-colors duration-75",
        active
          ? "text-[var(--text-primary)]"
          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
      )}
    >
      {active && (
        <motion.div
          layoutId="gc-sidebar-active"
          className="absolute inset-0 rounded-[var(--radius-sm)] bg-[var(--bg-surface-hover)]"
          transition={spring}
        />
      )}
      {isFork && (
        <GitFork size={11} className="relative z-[1] shrink-0 text-[var(--text-muted)]" />
      )}
      <div className="relative z-[1] flex-1 min-w-0">
        <span className="text-[12px] font-medium truncate block leading-tight">
          {conversation.title}
        </span>
        <span className="text-[10px] text-[var(--text-muted)] leading-tight">
          {formatRelative(conversation.updated_at)}
        </span>
      </div>
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className={cn(
          "relative z-[1] flex h-5 w-5 items-center justify-center rounded-[var(--radius-sm)]",
          "opacity-0 group-hover:opacity-100",
          "text-[var(--text-muted)] hover:text-[var(--color-soft-red)]",
          "hover:bg-[var(--color-soft-red)]/10",
          "transition-all duration-100",
        )}
      >
        <Trash size={11} />
      </motion.button>
    </motion.div>
  );
}

// ─── Fork lineage banner ──────────────────────────────────────────────────────

function ForkBanner({
  parentTitle,
  onNavigateToParent,
}: {
  parentTitle: string;
  onNavigateToParent: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springFast}
      className="flex justify-center py-3"
    >
      <button
        onClick={onNavigateToParent}
        className={cn(
          "flex items-center gap-1.5 rounded-full px-3 py-1",
          "border border-[var(--border-hairline)]",
          "bg-[var(--bg-surface)] hover:bg-[var(--bg-surface-hover)]",
          "text-[11px] text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
          "transition-all duration-100",
          "shadow-[var(--shadow-xs)]",
        )}
      >
        <GitFork size={11} className="text-[var(--text-muted)]" />
        <span>
          Forked from <span className="font-medium text-[var(--text-secondary)]">{parentTitle}</span>
        </span>
      </button>
    </motion.div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function ChatEmptyState({ onSend }: { onSend: (msg: string) => void }) {
  const greeting = useMemo(getGreeting, []);

  return (
    <div className="flex-1 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...spring, delay: 0.04 }}
        className="text-center max-w-md w-full"
      >
        <div className="flex justify-center mb-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)]">
            <LeverMark size={20} className="text-[var(--text-primary)]" />
          </div>
        </div>

        <h1
          className="text-[20px] font-semibold text-[var(--text-primary)] mb-1 tracking-[-0.03em]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {greeting}
        </h1>
        <p className="text-[12px] text-[var(--text-muted)] mb-8 leading-relaxed">
          Ask me anything. I remember context across conversations.
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          {SUGGESTIONS.map((s, i) => (
            <motion.button
              key={s.label}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: 0.08 + i * 0.03 }}
              onClick={() => onSend(s.message)}
              whileTap={{ scale: 0.97 }}
              className={cn(
                "rounded-full border border-[var(--border-subtle)] px-3 py-1.5 text-[12px]",
                "text-[var(--text-secondary)]",
                "hover:border-[var(--color-lever-orange)] hover:text-[var(--color-lever-orange)]",
                "hover:bg-[var(--color-lever-orange)]/5",
                "transition-all duration-100",
              )}
            >
              {s.label}
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Message component ───────────────────────────────────────────────────────

function ChatMessageBubble({
  message,
  isLast,
  isStreaming,
  onSaveMemory,
  onFork,
}: {
  message: GCMessage;
  isLast: boolean;
  isStreaming: boolean;
  onSaveMemory: (content: string) => void;
  onFork: (messageId: string) => void | Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [forking, setForking] = useState(false);
  const isUser = message.role === "user";
  const isAssistantStreaming = isLast && isStreaming && !isUser;
  const isEmpty = !message.content;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [message.content]);

  const handleSave = useCallback(() => {
    onSaveMemory(message.content);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }, [message.content, onSaveMemory]);

  const handleFork = useCallback(async () => {
    setForking(true);
    try {
      await onFork(message.id);
    } finally {
      setForking(false);
    }
  }, [message.id, onFork]);

  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex justify-end group"
      >
        <div className="flex flex-col items-end gap-1">
          <div
            className={cn(
              "max-w-[85%] sm:max-w-[70%]",
              "rounded-[var(--radius-lg)]",
              "bg-[var(--bg-surface-hover)]",
              "px-3.5 py-2.5 text-[13px] text-[var(--text-primary)] leading-relaxed",
            )}
            style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word", wordBreak: "break-word" }}
          >
            {message.content}
          </div>
          {!isStreaming && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-100 pr-1">
              <button
                onClick={handleFork}
                disabled={forking}
                className={cn(
                  "flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[10px]",
                  "text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-secondary)]",
                  "transition-colors duration-75",
                  forking && "opacity-50",
                )}
              >
                {forking ? (
                  <CircleNotch size={10} className="animate-spin" />
                ) : (
                  <GitFork size={10} />
                )}
                Fork
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="flex gap-2.5 group"
    >
      <div className="shrink-0 mt-0.5">
        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-[var(--bg-surface-hover)] text-[var(--text-muted)]">
          <LeverMark size={11} />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {isEmpty && isAssistantStreaming ? (
          <div className="py-1">
            <span className="thinking-shimmer text-[13px] font-medium">
              thinking...
            </span>
          </div>
        ) : (
          <MarkdownContent content={message.content} />
        )}

        {isAssistantStreaming && message.content && (
          <span className="inline-block w-0.5 h-3.5 ml-0.5 bg-[var(--text-primary)] animate-pulse rounded-full" />
        )}

        {!isAssistantStreaming && message.content && (
          <div className="mt-1.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-100">
            <button
              onClick={handleCopy}
              className={cn(
                "flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[10px]",
                "text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-secondary)]",
                "transition-colors duration-75",
              )}
            >
              {copied ? (
                <Check size={10} weight="bold" className="text-[var(--color-signal-green)]" />
              ) : (
                <Copy size={10} />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={handleSave}
              className={cn(
                "flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[10px]",
                "text-[var(--text-muted)] hover:bg-[var(--color-lever-orange)]/10 hover:text-[var(--color-lever-orange)]",
                "transition-colors duration-75",
              )}
            >
              {saved ? (
                <Check size={10} weight="bold" className="text-[var(--color-signal-green)]" />
              ) : (
                <BookmarkSimple size={10} />
              )}
              {saved ? "Saved" : "Remember"}
            </button>
            <button
              onClick={handleFork}
              disabled={forking}
              className={cn(
                "flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[10px]",
                "text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-secondary)]",
                "transition-colors duration-75",
                forking && "opacity-50",
              )}
            >
              {forking ? (
                <CircleNotch size={10} className="animate-spin" />
              ) : (
                <GitFork size={10} />
              )}
              Fork
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Model picker ────────────────────────────────────────────────────────────

function ModelPicker() {
  const { availableProviders, selectedProvider, selectedModel, setProvider, fetchModels } =
    useAIStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (availableProviders.length === 0) fetchModels();
  }, [availableProviders.length, fetchModels]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const currentProvider = availableProviders.find((p) => p.id === selectedProvider);
  const currentModel = currentProvider?.models.find((m) => m.id === selectedModel);
  const displayName = currentModel?.name || selectedModel || "Default";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-1",
          "text-[10px] font-medium",
          "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
          "hover:bg-[var(--bg-surface-hover)]",
          "transition-colors duration-100",
        )}
      >
        <Lightning size={10} weight="fill" className="text-[var(--color-lever-orange)]" />
        <span className="max-w-[120px] truncate">{displayName}</span>
        <CaretDown size={8} className={cn("transition-transform", open && "rotate-180")} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={springFast}
            className={cn(
              "absolute bottom-full left-0 mb-1.5 z-50",
              "w-56 max-h-[320px] overflow-y-auto",
              "rounded-[var(--radius-xl)] p-1.5",
              "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
              "shadow-[var(--shadow-xl)]",
            )}
          >
            {availableProviders.map((provider) => (
              <div key={provider.id}>
                <div className="px-2 py-1">
                  <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                    {provider.name}
                  </span>
                </div>
                {provider.models.map((model) => {
                  const isActive =
                    provider.id === selectedProvider && model.id === selectedModel;
                  return (
                    <button
                      key={model.id}
                      onClick={() => {
                        setProvider(provider.id, model.id);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex items-center gap-2 w-full rounded-[var(--radius-md)] px-2.5 py-1.5 text-left",
                        "text-[11px] transition-colors duration-75",
                        isActive
                          ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)] font-medium"
                          : "text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
                      )}
                    >
                      <span className="flex-1 truncate">{model.name}</span>
                      {model.tier === "free" && (
                        <span className="text-[8px] font-bold uppercase tracking-wider text-[var(--color-signal-green)]">
                          Free
                        </span>
                      )}
                      {isActive && (
                        <Check size={10} weight="bold" className="text-[var(--color-lever-orange)]" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Chat input ──────────────────────────────────────────────────────────────

function ChatInput({
  onSend,
  isStreaming,
  onStop,
}: {
  onSend: (content: string) => void;
  isStreaming: boolean;
  onStop: () => void;
}) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const autoResize = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 180) + "px";
  }, []);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming) return;
    onSend(text);
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
  }, [input, isStreaming, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const hasContent = input.trim().length > 0;

  return (
    <div className="shrink-0 px-4 pb-4 pt-2">
      <div className="max-w-2xl mx-auto">
        <div
          className={cn(
            "rounded-[var(--radius-2xl)]",
            "border border-[var(--border-subtle)]",
            "bg-[var(--bg-surface)]",
            "shadow-[var(--shadow-sm)]",
            "focus-within:border-[var(--text-primary)]/15",
            "focus-within:shadow-[var(--shadow-md)]",
            "transition-all duration-150",
          )}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              autoResize();
            }}
            onKeyDown={handleKeyDown}
            placeholder="Message lever..."
            rows={1}
            className={cn(
              "w-full resize-none bg-transparent",
              "text-[13px] text-[var(--text-primary)]",
              "placeholder:text-[var(--text-muted)]",
              "outline-none",
              "px-4 pt-3 pb-1",
              "max-h-44",
            )}
            style={{ fontFamily: "var(--font-sans)" }}
          />

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between px-3 pb-2 pt-0.5">
            <ModelPicker />

            <motion.button
              onClick={
                isStreaming ? onStop : hasContent ? handleSend : undefined
              }
              disabled={!isStreaming && !hasContent}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              transition={springFast}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-full",
                "transition-all duration-150",
                isStreaming
                  ? "bg-[var(--bg-surface-hover)] border border-[var(--border-subtle)] text-[var(--text-secondary)]"
                  : hasContent
                    ? "bg-[var(--text-primary)] text-[var(--text-inverse)] shadow-sm"
                    : "bg-[var(--bg-base)] text-[var(--text-muted)] border border-[var(--border-hairline)]",
              )}
            >
              <AnimatePresence mode="wait" initial={false}>
                {isStreaming ? (
                  <motion.div
                    key="stop"
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.4, opacity: 0 }}
                    transition={{ duration: 0.12 }}
                  >
                    <Stop size={13} weight="fill" />
                  </motion.div>
                ) : hasContent ? (
                  <motion.div
                    key="arrow"
                    initial={{ scale: 0.5, opacity: 0, rotate: -90 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    exit={{ scale: 0.5, opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.12 }}
                  >
                    <ArrowUp size={13} weight="bold" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="mic"
                    initial={{ scale: 0.5, opacity: 0, rotate: 90 }}
                    animate={{ scale: 1, opacity: 1, rotate: 0 }}
                    exit={{ scale: 0.5, opacity: 0, rotate: -90 }}
                    transition={{ duration: 0.12 }}
                  >
                    <Microphone size={13} weight="bold" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Memory panel ────────────────────────────────────────────────────────────

function MemoryPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { memoryItems, memoryLoaded, deleteMemory, togglePinMemory } =
    useGlobalChatStore();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return memoryItems;
    const q = search.toLowerCase();
    return memoryItems.filter((m) => m.content.toLowerCase().includes(q));
  }, [memoryItems, search]);

  const pinned = filtered.filter((m) => m.pinned);
  const unpinned = filtered.filter((m) => !m.pinned);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 300, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={spring}
          className="shrink-0 flex flex-col h-full overflow-hidden border-l border-[var(--border-hairline)] bg-[var(--bg-surface)]"
        >
          <div className="flex items-center gap-2 px-3 pt-3 pb-2 shrink-0">
            <BookmarkSimple size={14} className="text-[var(--text-muted)]" />
            <span
              className="text-[13px] font-semibold text-[var(--text-primary)] flex-1"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Memory
            </span>
            <span className="text-[10px] text-[var(--text-muted)]">
              {memoryItems.length} items
            </span>
            <button
              onClick={onClose}
              className="flex h-5 w-5 items-center justify-center rounded-[var(--radius-sm)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)] transition-colors"
            >
              <X size={11} weight="bold" />
            </button>
          </div>

          {/* Memory search */}
          <div className="px-2 pb-1.5 shrink-0">
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-sm)]",
                "border border-[var(--border-hairline)] bg-[var(--bg-base)]",
                "px-2 py-1 text-[11px]",
                "focus-within:border-[var(--border-subtle)]",
                "transition-colors duration-100",
              )}
            >
              <MagnifyingGlass size={11} className="shrink-0 text-[var(--text-muted)]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search memory..."
                className="flex-1 bg-transparent text-[11px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none min-w-0"
              />
            </div>
          </div>

          {/* Memory list */}
          <div className="flex-1 overflow-y-auto px-2 py-1">
            {!memoryLoaded ? (
              <div className="flex items-center justify-center py-8">
                <CircleNotch size={16} className="animate-spin text-[var(--text-muted)]" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-8 text-center">
                <p className="text-[11px] text-[var(--text-muted)]">
                  {search ? "No matching memories" : "No memories yet. Save responses to build your memory."}
                </p>
              </div>
            ) : (
              <>
                {pinned.length > 0 && (
                  <MemorySection
                    label="Pinned"
                    items={pinned}
                    onDelete={deleteMemory}
                    onTogglePin={togglePinMemory}
                  />
                )}
                {unpinned.length > 0 && (
                  <MemorySection
                    label={pinned.length > 0 ? "Recent" : undefined}
                    items={unpinned}
                    onDelete={deleteMemory}
                    onTogglePin={togglePinMemory}
                  />
                )}
              </>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

function MemorySection({
  label,
  items,
  onDelete,
  onTogglePin,
}: {
  label?: string;
  items: MemoryItem[];
  onDelete: (id: string) => void;
  onTogglePin: (id: string) => void;
}) {
  return (
    <div className="mb-2">
      {label && (
        <div className="px-1 py-1">
          <span className="text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
            {label}
          </span>
        </div>
      )}
      <div className="flex flex-col gap-1">
        {items.map((item) => (
          <MemoryCard
            key={item.id}
            item={item}
            onDelete={() => onDelete(item.id)}
            onTogglePin={() => onTogglePin(item.id)}
          />
        ))}
      </div>
    </div>
  );
}

function MemoryCard({
  item,
  onDelete,
  onTogglePin,
}: {
  item: MemoryItem;
  onDelete: () => void;
  onTogglePin: () => void;
}) {
  return (
    <div
      className={cn(
        "group rounded-[var(--radius-md)] p-2.5",
        "border border-[var(--border-hairline)]",
        "bg-[var(--bg-base)]",
        "hover:border-[var(--border-subtle)]",
        "transition-colors duration-100",
      )}
    >
      <div className="text-[11px] text-[var(--text-secondary)] leading-relaxed line-clamp-4 [&_.prose-chat]:text-[11px]">
        <MarkdownContent content={item.content} />
      </div>
      <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={onTogglePin}
          className={cn(
            "flex items-center gap-0.5 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[9px]",
            item.pinned
              ? "text-[var(--color-lever-orange)] bg-[var(--color-lever-orange)]/10"
              : "text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]",
            "transition-colors",
          )}
        >
          <PushPin size={9} weight={item.pinned ? "fill" : "regular"} />
          {item.pinned ? "Unpin" : "Pin"}
        </button>
        <button
          onClick={onDelete}
          className="flex items-center gap-0.5 rounded-[var(--radius-sm)] px-1.5 py-0.5 text-[9px] text-[var(--text-muted)] hover:text-[var(--color-soft-red)] hover:bg-[var(--color-soft-red)]/10 transition-colors"
        >
          <Trash size={9} />
          Delete
        </button>
        {item.tags && item.tags.length > 0 && (
          <div className="flex gap-0.5 ml-auto">
            {item.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[var(--bg-surface-hover)] px-1.5 py-px text-[8px] text-[var(--text-muted)]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function GlobalChatPage() {
  useDocumentTitle("Chat");
  const toast = useToast();

  const {
    messages,
    isStreaming,
    currentConversationId,
    conversations,
    sidebarOpen,
    memoryPanelOpen,
    setSidebarOpen,
    setMemoryPanelOpen,
    fetchConversations,
    sendMessage,
    startNewConversation,
    stopStreaming,
    saveToMemory,
    forkConversation,
    navigateToParent,
    conversationsLoaded,
  } = useGlobalChatStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Initial data fetch ──
  useEffect(() => {
    if (!conversationsLoaded) fetchConversations();
  }, [conversationsLoaded, fetchConversations]);

  // ── Auto-scroll ──
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && e.key === "n") {
        e.preventDefault();
        startNewConversation();
      }
      if (isMod && e.key === "b") {
        e.preventDefault();
        setSidebarOpen(!sidebarOpen);
      }
      if (isMod && e.key === "m") {
        e.preventDefault();
        setMemoryPanelOpen(!memoryPanelOpen);
      }
      if (e.key === "Escape") {
        if (memoryPanelOpen) setMemoryPanelOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [sidebarOpen, memoryPanelOpen, setSidebarOpen, setMemoryPanelOpen, startNewConversation]);

  // ── Send handler ──
  const handleSend = useCallback(
    (content: string) => {
      sendMessage(content);
    },
    [sendMessage],
  );

  // ── Save to memory ──
  const handleSaveMemory = useCallback(
    async (content: string) => {
      try {
        await saveToMemory(content, currentConversationId || undefined);
        toast.success("Saved to memory", "This will be remembered in future conversations.");
      } catch {
        toast.error("Save failed", "Could not save to memory.");
      }
    },
    [saveToMemory, currentConversationId, toast],
  );

  // ── Fork ──
  const handleFork = useCallback(
    async (messageId: string) => {
      try {
        await forkConversation(messageId);
        toast.success("Conversation forked", "Continue from here in a new direction.");
      } catch {
        toast.error("Fork failed", "Could not fork the conversation.");
      }
    },
    [forkConversation, toast],
  );

  const currentConv = conversations.find((c) => c.id === currentConversationId);
  const isFork = !!currentConv?.forked_from_id;
  const parentTitle = currentConv?.forked_from_title || "Original conversation";

  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-dvh w-dvw overflow-hidden bg-[var(--bg-primary)]">
      {/* Left sidebar */}
      <ConversationSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Top bar */}
        <header className="flex items-center gap-2 shrink-0 h-10 px-3 border-b border-[var(--border-hairline)] bg-[var(--bg-primary)]">
          {!sidebarOpen && (
            <motion.button
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => setSidebarOpen(true)}
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)]",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-100",
              )}
            >
              <SidebarSimple size={14} />
            </motion.button>
          )}
          {!sidebarOpen && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={startNewConversation}
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)]",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "hover:bg-[var(--bg-surface-hover)]",
                "transition-colors duration-100",
              )}
              title="New chat"
            >
              <Plus size={13} weight="bold" />
            </motion.button>
          )}

          <div className="flex-1" />

          {/* Memory toggle */}
          <motion.button
            onClick={() => setMemoryPanelOpen(!memoryPanelOpen)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.92 }}
            className={cn(
              "flex h-7 items-center gap-1.5 rounded-[var(--radius-md)] px-2.5",
              "text-[11px] font-medium",
              memoryPanelOpen
                ? "bg-[var(--bg-surface-hover)] text-[var(--text-primary)]"
                : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-100",
            )}
          >
            <BookmarkSimple size={13} weight={memoryPanelOpen ? "fill" : "regular"} />
            <span className="hidden sm:inline">Memory</span>
          </motion.button>
        </header>

        {/* Chat content */}
        <div className="flex-1 flex flex-col min-h-0">
          {isEmpty ? (
            <ChatEmptyState onSend={handleSend} />
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
                {isFork && (
                  <ForkBanner
                    parentTitle={parentTitle}
                    onNavigateToParent={navigateToParent}
                  />
                )}
                <AnimatePresence initial={false}>
                  {messages.map((msg, idx) => (
                    <ChatMessageBubble
                      key={msg.id}
                      message={msg}
                      isLast={idx === messages.length - 1}
                      isStreaming={isStreaming}
                      onSaveMemory={handleSaveMemory}
                      onFork={handleFork}
                    />
                  ))}
                </AnimatePresence>
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}

          <ChatInput
            onSend={handleSend}
            isStreaming={isStreaming}
            onStop={stopStreaming}
          />
        </div>
      </div>

      {/* Memory panel */}
      <MemoryPanel
        open={memoryPanelOpen}
        onClose={() => setMemoryPanelOpen(false)}
      />
    </div>
  );
}
